import { useCallback, useEffect, useState } from "react";
import { api, apiError, rupiah, tanggal } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { ErpLayout, Card, Table, Timeline } from "@/erp/shell";
import { MediaGalleryUploadField, MediaUploadField } from "@/erp/MediaUploadField";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, X } from "lucide-react";

const inp = "w-full rounded-lg border border-[#E5DEC9] px-3 py-2 text-sm outline-none focus:border-[#C5A028]";
const label = "block text-xs font-bold uppercase tracking-wider text-[#4A4844] mb-1";

const EMPTY = {
  name: "", slug: "", category_slug: "umrah-reguler", short_description: "", description: "",
  hero_image: "", gallery: "", duration_days: 9, quota: 45, departure_city: "Jakarta",
  airline: "", hotel_makkah: "", hotel_madinah: "", room_types: "Quad, Triple, Double",
  facilities: "", terms: "", base_price: 0, promo_price: 0, promo_valid_until: "",
  commission_pool_per_pilgrim: 0, status: "draft", seo_title: "", seo_description: "", og_image: "",
};

const toPayload = (f) => ({
  ...f,
  duration_days: Number(f.duration_days) || 0,
  quota: Number(f.quota) || 0,
  base_price: Number(f.base_price) || 0,
  promo_price: Number(f.promo_price) || 0,
  commission_pool_per_pilgrim: Number(f.commission_pool_per_pilgrim) || 0,
  gallery: String(f.gallery || "").split(/\n|,/).map((s) => s.trim()).filter(Boolean),
  room_types: String(f.room_types || "").split(",").map((s) => s.trim()).filter(Boolean),
  facilities: String(f.facilities || "").split(/\n|,/).map((s) => s.trim()).filter(Boolean),
  faqs: f.faqs || [],
});

export default function ProductsAdmin() {
  const { can } = useAuth();
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("-created_at");
  const [page, setPage] = useState(1);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  // FAQ editing state (used when form is open)
  const [faqDraft, setFaqDraft] = useState({ question: "", answer: "" });
  const [editFaqIdx, setEditFaqIdx] = useState(null);
  const [editFaqData, setEditFaqData] = useState({ question: "", answer: "" });

  const load = useCallback(() => {
    api.get("/admin/products", { params: { q: q || undefined, status: status || undefined, sort, page, page_size: 8 } })
      .then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [q, status, sort, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/public/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const openNew = () => { setForm({ ...EMPTY, _id: null }); setFaqDraft({ question: "", answer: "" }); setEditFaqIdx(null); };
  const openEdit = async (p) => {
    const { data: full } = await api.get(`/admin/products/${p.id}`);
    setForm({
      ...EMPTY, ...full, _id: full.id,
      gallery: (full.gallery || []).join("\n"),
      room_types: (full.room_types || []).join(", "),
      facilities: (full.facilities || []).join("\n"),
      faqs: full.faqs || [],
    });
    setFaqDraft({ question: "", answer: "" });
    setEditFaqIdx(null);
  };

  const addFaq = () => {
    if (!faqDraft.question.trim()) return;
    setForm((f) => ({ ...f, faqs: [...(f.faqs || []), { question: faqDraft.question.trim(), answer: faqDraft.answer.trim() }] }));
    setFaqDraft({ question: "", answer: "" });
  };
  const delFaq = (idx) => {
    setForm((f) => ({ ...f, faqs: (f.faqs || []).filter((_, i) => i !== idx) }));
    if (editFaqIdx === idx) setEditFaqIdx(null);
  };
  const openEditFaq = (idx) => { setEditFaqIdx(idx); setEditFaqData({ ...form.faqs[idx] }); };
  const saveEditFaq = (idx) => {
    setForm((f) => {
      const faqs = [...(f.faqs || [])];
      faqs[idx] = { question: editFaqData.question.trim(), answer: editFaqData.answer.trim() };
      return { ...f, faqs };
    });
    setEditFaqIdx(null);
  };

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    const { _id, ...rest } = form;
    try {
      if (_id) await api.put(`/admin/products/${_id}`, toPayload(rest));
      else await api.post("/admin/products", toPayload(rest));
      toast.success(_id ? "Paket diperbarui" : "Paket dibuat");
      setForm(null); load();
    } catch (e2) { toast.error(apiError(e2)); } finally { setBusy(false); }
  };

  const publish = async (p, s) => {
    try { await api.patch(`/admin/products/${p.id}/status`, { status: s }); toast.success(`Status: ${s}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const del = async (p) => {
    if (!window.confirm(`Hapus paket "${p.name}"?`)) return;
    try { await api.delete(`/admin/products/${p.id}`); toast.success("Paket dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const showDetail = async (p) => {
    const { data: full } = await api.get(`/admin/products/${p.id}`);
    setDetail(full);
  };

  const pages = Math.max(1, Math.ceil(data.total / 8));

  return (
    <ErpLayout title="Produk & Paket" breadcrumb={["Produk & Paket"]}
      actions={can("product.write") && (
        <button onClick={openNew} data-testid="product-new-btn" className="btn-gold flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold">
          <Plus size={16} /> Paket Baru
        </button>)}>
      <Card className="mb-5">
        <div className="grid gap-3 sm:grid-cols-4">
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Cari nama paket..."
            data-testid="product-search" className={inp} />
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} data-testid="product-status-filter" className={inp}>
            <option value="">Semua status</option><option value="draft">Draft</option>
            <option value="published">Published</option><option value="archived">Archived</option>
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="product-sort" className={inp}>
            <option value="-created_at">Terbaru</option><option value="created_at">Terlama</option>
            <option value="name">Nama A-Z</option><option value="-base_price">Harga tertinggi</option>
          </select>
          <div className="flex items-center text-sm text-[#8A867C]">{data.total} paket ditemukan</div>
        </div>
      </Card>

      <Table testid="product-table" columns={["Nama", "Kategori", "Harga", "Kuota", "Status", "Aksi"]}
        rows={data.items.map((p) => (
          <tr key={p.id} className="border-t border-[#F4EFE6]" data-testid={`product-row-${p.id}`}>
            <td className="p-4">
              <div className="font-semibold text-[#121212]">{p.name}</div>
              <div className="text-xs text-[#8A867C]">/{p.slug}</div>
              {p.commission_pool_per_pilgrim > 0 && (
                <div className="mt-1 text-xs font-semibold text-[#8C6D1F]">Pool komisi: {rupiah(p.commission_pool_per_pilgrim)}</div>)}
            </td>
            <td className="p-4">{p.category_name}</td>
            <td className="p-4">{rupiah(p.promo_price > 0 ? p.promo_price : p.base_price)}</td>
            <td className="p-4">{p.quota}</td>
            <td className="p-4">
              <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${p.status === "published" ? "bg-[#F5F1E8] text-[#15803D]" : p.status === "draft" ? "bg-[#F4EFE6] text-[#B45309]" : "bg-[#F1F1F1] text-[#8A867C]"}`}>{p.status}</span>
            </td>
            <td className="p-4">
              <div className="flex gap-2">
                <button onClick={() => showDetail(p)} data-testid={`product-view-${p.id}`} className="rounded-lg border border-[#E5DEC9] p-2"><Eye size={14} /></button>
                {can("product.write") && <>
                  <button onClick={() => openEdit(p)} data-testid={`product-edit-${p.id}`} className="rounded-lg border border-[#E5DEC9] p-2"><Pencil size={14} /></button>
                  {p.status !== "published" ? (
                    <button onClick={() => publish(p, "published")} data-testid={`product-publish-${p.id}`} className="btn-gold rounded-lg px-3 py-1.5 text-xs font-bold">Publish</button>
                  ) : (
                    <button onClick={() => publish(p, "archived")} data-testid={`product-archive-${p.id}`} className="rounded-lg border border-[#E5DEC9] px-3 py-1.5 text-xs">Arsipkan</button>)}
                  <button onClick={() => del(p)} data-testid={`product-delete-${p.id}`} className="rounded-lg border border-[#F0C4C4] p-2 text-[#B91C1C]"><Trash2 size={14} /></button>
                </>}
              </div>
            </td>
          </tr>))} />

      <div className="mt-4 flex items-center gap-2">
        <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="product-prev-page"
          className="rounded-lg border border-[#E5DEC9] bg-white px-3 py-2 text-sm disabled:opacity-40">Sebelumnya</button>
        <span className="text-sm text-[#4A4844]">Halaman {page} dari {pages}</span>
        <button disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="product-next-page"
          className="rounded-lg border border-[#E5DEC9] bg-white px-3 py-2 text-sm disabled:opacity-40">Berikutnya</button>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
          <form onSubmit={save} data-testid="product-form" className="my-8 w-full max-w-3xl rounded-2xl bg-white p-7">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-[#141414]">{form._id ? "Edit Paket" : "Paket Baru"}</h2>
              <button type="button" onClick={() => setForm(null)} data-testid="product-form-close"><X size={18} /></button>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2"><label className={label}>Nama Paket</label>
                <input required className={inp} data-testid="pf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><label className={label}>Kategori</label>
                <select className={inp} data-testid="pf-category" value={form.category_slug} onChange={(e) => setForm({ ...form, category_slug: e.target.value })}>
                  {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select></div>
              <div><label className={label}>Status</label>
                <select className={inp} data-testid="pf-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option>
                </select></div>
              <div className="sm:col-span-2"><label className={label}>Deskripsi Singkat</label>
                <input className={inp} data-testid="pf-short" value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className={label}>Deskripsi Lengkap</label>
                <textarea rows={4} className={inp} data-testid="pf-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div><label className={label}>Durasi (hari)</label><input type="number" className={inp} data-testid="pf-duration" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} /></div>
              <div><label className={label}>Kuota</label><input type="number" className={inp} data-testid="pf-quota" value={form.quota} onChange={(e) => setForm({ ...form, quota: e.target.value })} /></div>
              <div><label className={label}>Kota Keberangkatan</label><input className={inp} data-testid="pf-city" value={form.departure_city} onChange={(e) => setForm({ ...form, departure_city: e.target.value })} /></div>
              <div><label className={label}>Maskapai</label><input className={inp} data-testid="pf-airline" value={form.airline} onChange={(e) => setForm({ ...form, airline: e.target.value })} /></div>
              <div><label className={label}>Hotel Makkah</label><input className={inp} data-testid="pf-hotel-makkah" value={form.hotel_makkah} onChange={(e) => setForm({ ...form, hotel_makkah: e.target.value })} /></div>
              <div><label className={label}>Hotel Madinah</label><input className={inp} data-testid="pf-hotel-madinah" value={form.hotel_madinah} onChange={(e) => setForm({ ...form, hotel_madinah: e.target.value })} /></div>
              <div><label className={label}>Harga Dasar (IDR)</label><input type="number" className={inp} data-testid="pf-price" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} /></div>
              <div><label className={label}>Harga Promo (IDR)</label><input type="number" className={inp} data-testid="pf-promo" value={form.promo_price} onChange={(e) => setForm({ ...form, promo_price: e.target.value })} /></div>
              <div><label className={label}>Promo Berlaku Hingga</label><input type="date" className={inp} data-testid="pf-promo-until" value={form.promo_valid_until || ""} onChange={(e) => setForm({ ...form, promo_valid_until: e.target.value })} /></div>
              {can("commission.write") && (
                <div><label className={label}>Pool Komisi per Jamaah (IDR)</label>
                  <input type="number" className={inp} data-testid="pf-commission" value={form.commission_pool_per_pilgrim} onChange={(e) => setForm({ ...form, commission_pool_per_pilgrim: e.target.value })} /></div>)}
              <div><label className={label}>Tipe Kamar (pisahkan koma)</label><input className={inp} data-testid="pf-rooms" value={form.room_types} onChange={(e) => setForm({ ...form, room_types: e.target.value })} /></div>
              <div className="sm:col-span-2">
                <MediaUploadField label="Gambar Utama" testId="pf-hero" context="product"
                  value={form.hero_image} onChange={(url) => setForm({ ...form, hero_image: url })}
                  help="Upload gambar paket ke Object Storage atau tempel URL eksternal." />
              </div>
              <div className="sm:col-span-2">
                <MediaGalleryUploadField label="Galeri" testId="pf-gallery" context="product"
                  value={form.gallery} onChange={(urls) => setForm({ ...form, gallery: urls })} />
              </div>
              <div className="sm:col-span-2"><label className={label}>Fasilitas (satu per baris)</label><textarea rows={3} className={inp} data-testid="pf-facilities" value={form.facilities} onChange={(e) => setForm({ ...form, facilities: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className={label}>Syarat & Ketentuan</label><textarea rows={2} className={inp} data-testid="pf-terms" value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} /></div>

              {/* ── FAQ ── */}
              <div className="sm:col-span-2">
                <label className={label}>FAQ</label>
                <div className="space-y-2 mt-1">
                  {(form.faqs || []).map((faq, idx) => (
                    <div key={idx} data-testid={`faq-item-${idx}`}>
                      {editFaqIdx === idx ? (
                        <div className="rounded-lg border-2 border-[#C5A028] bg-[#FFFDF5] p-3 space-y-2">
                          <input className={inp} data-testid="faq-edit-q" placeholder="Pertanyaan" value={editFaqData.question}
                            onChange={(e) => setEditFaqData({ ...editFaqData, question: e.target.value })} />
                          <textarea rows={2} className={inp} data-testid="faq-edit-a" placeholder="Jawaban" value={editFaqData.answer}
                            onChange={(e) => setEditFaqData({ ...editFaqData, answer: e.target.value })} />
                          <div className="flex gap-2 justify-end">
                            <button type="button" onClick={() => setEditFaqIdx(null)} className="rounded-lg border border-[#E5DEC9] px-3 py-1.5 text-xs font-semibold">Batal</button>
                            <button type="button" onClick={() => saveEditFaq(idx)} data-testid={`faq-save-${idx}`} className="btn-gold rounded-lg px-4 py-1.5 text-xs font-bold">Simpan</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between rounded-lg border border-[#E5DEC9] bg-white p-3 text-sm">
                          <div>
                            <div className="font-semibold text-[#141414]">{faq.question}</div>
                            {faq.answer && <div className="mt-0.5 text-xs text-[#8A867C]">{faq.answer}</div>}
                          </div>
                          <div className="flex gap-1 shrink-0 ml-3">
                            <button type="button" onClick={() => openEditFaq(idx)} data-testid={`faq-edit-btn-${idx}`}
                              className="rounded-lg border border-[#E5DEC9] p-1.5 text-[#4A4844] hover:bg-[#F4EFE6]"><Pencil size={12} /></button>
                            <button type="button" onClick={() => delFaq(idx)} data-testid={`faq-delete-${idx}`}
                              className="rounded-lg border border-[#F0C4C4] p-1.5 text-[#B91C1C]"><Trash2 size={12} /></button>
                          </div>
                        </div>)}
                    </div>))}
                  {(form.faqs || []).length === 0 && <p className="text-xs text-[#8A867C]">Belum ada FAQ.</p>}
                </div>
                {/* Tambah FAQ baru */}
                <div className="mt-3 rounded-xl bg-[#F4EFE6] p-3 space-y-2">
                  <input className={inp} data-testid="faq-new-q" placeholder="Pertanyaan baru..." value={faqDraft.question}
                    onChange={(e) => setFaqDraft({ ...faqDraft, question: e.target.value })} />
                  <textarea rows={2} className={inp} data-testid="faq-new-a" placeholder="Jawaban..." value={faqDraft.answer}
                    onChange={(e) => setFaqDraft({ ...faqDraft, answer: e.target.value })} />
                  <div className="flex justify-end">
                    <button type="button" onClick={addFaq} data-testid="faq-add-btn"
                      className="rounded-lg border border-[#141414] px-4 py-1.5 text-xs font-bold">
                      <Plus size={12} className="mr-1 inline" />Tambah FAQ
                    </button>
                  </div>
                </div>
              </div>

              <div><label className={label}>SEO Title</label><input className={inp} data-testid="pf-seo-title" value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} /></div>
              <div>
                <MediaUploadField label="Open Graph Image" testId="pf-og" context="product"
                  value={form.og_image} onChange={(url) => setForm({ ...form, og_image: url })} />
              </div>
              <div className="sm:col-span-2"><label className={label}>Meta Description</label><input className={inp} data-testid="pf-seo-desc" value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} /></div>
            </div>
            <div className="mt-7 flex gap-3">
              <button disabled={busy} data-testid="product-save-btn" className="btn-gold rounded-xl px-6 py-3 text-sm font-bold">{busy ? "Menyimpan..." : "Simpan Paket"}</button>
              <button type="button" onClick={() => setForm(null)} className="rounded-xl border border-[#E5DEC9] px-6 py-3 text-sm font-semibold">Batal</button>
            </div>
          </form>
        </div>)}

      {detail && <ProductDetail p={detail} onClose={() => setDetail(null)} onChanged={async () => {
        const { data: full } = await api.get(`/admin/products/${detail.id}`); setDetail(full); load();
      }} />}
    </ErpLayout>
  );
}

function ProductDetail({ p, onClose, onChanged }) {
  const { can } = useAuth();
  // Inisialisasi harga per tipe kamar dari room_types produk
  const roomTypes = (p.room_types || []).length > 0 ? p.room_types : ["Quad"];
  const initPrices = Object.fromEntries(roomTypes.map((rt) => [rt, p.base_price ?? ""]));
  const [dep, setDep] = useState({
    depart_date: "", return_date: "", seats_total: p.quota, airline: p.airline,
    prices: initPrices,
  });
  const [editDep, setEditDep] = useState(null); // {id, depart_date, return_date, seats_total, airline, prices:{}}

  const openEditDep = (d) => {
    const pricesMap = Object.fromEntries(
      (d.prices || []).map((x) => [x.room_type, x.price])
    );
    setEditDep({
      id: d.id,
      depart_date: d.depart_date || "",
      return_date: d.return_date || "",
      seats_total: d.seats_total,
      airline: d.airline || "",
      prices: pricesMap,
    });
  };

  const saveEditDep = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/departures/${editDep.id}`, {
        product_id: p.id,
        depart_date: editDep.depart_date,
        return_date: editDep.return_date,
        seats_total: Number(editDep.seats_total),
        airline: editDep.airline,
        departure_city: p.departure_city,
        prices: roomTypes.map((rt) => ({ room_type: rt, price: Number(editDep.prices[rt] || 0) })),
      });
      toast.success("Jadwal berhasil diperbarui");
      setEditDep(null);
      onChanged();
    } catch (e2) { toast.error(apiError(e2)); }
  };

  const [itin, setItin] = useState({ day: "", title: "", description: "" });
  const [editItin, setEditItin] = useState(null); // {id, day, title, description}

  const addItinerary = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/itineraries", {
        product_id: p.id, day: Number(itin.day), title: itin.title, description: itin.description,
      });
      toast.success("Itinerary ditambahkan");
      setItin({ day: "", title: "", description: "" });
      onChanged();
    } catch (e2) { toast.error(apiError(e2)); }
  };
  const delItinerary = async (id) => {
    try { await api.delete(`/admin/itineraries/${id}`); toast.success("Itinerary dihapus"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveEditItin = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/itineraries/${editItin.id}`, {
        product_id: p.id, day: Number(editItin.day), title: editItin.title, description: editItin.description,
      });
      toast.success("Itinerary diperbarui");
      setEditItin(null);
      onChanged();
    } catch (e2) { toast.error(apiError(e2)); }
  };

  const addDeparture = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/departures", {
        product_id: p.id, depart_date: dep.depart_date, return_date: dep.return_date,
        seats_total: Number(dep.seats_total), airline: dep.airline, departure_city: p.departure_city,
        prices: roomTypes.map((rt) => ({ room_type: rt, price: Number(dep.prices[rt] || 0) })),
      });
      toast.success("Jadwal ditambahkan"); onChanged();
    } catch (e2) { toast.error(apiError(e2)); }
  };
  const delDeparture = async (id) => {
    try { await api.delete(`/admin/departures/${id}`); toast.success("Jadwal dihapus"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div data-testid="product-detail-panel" className="my-8 w-full max-w-3xl rounded-2xl bg-white p-7">
        <div className="flex items-start justify-between">
          <div>
            <div className="eyebrow">{p.category_name}</div>
            <h2 className="mt-1 font-display text-2xl font-bold text-[#141414]">{p.name}</h2>
          </div>
          <button onClick={onClose} data-testid="product-detail-close"><X size={18} /></button>
        </div>
        {p.commission_pool_per_pilgrim !== undefined && (
          <div className="mt-4 rounded-xl bg-[#FDF6E3] p-4 text-sm">
            <span className="eyebrow">Internal</span>
            <div className="mt-1 font-bold text-[#8C6D1F]">Pool komisi per jamaah: {rupiah(p.commission_pool_per_pilgrim)}</div>
          </div>)}
        <h3 className="mt-6 font-display text-lg font-semibold">Jadwal Keberangkatan</h3>
        <div className="mt-3 space-y-2">
          {(p.departures || []).map((d) => (
            <div key={d.id} data-testid={`departure-row-${d.id}`}>
              {editDep?.id === d.id ? (
                <form onSubmit={saveEditDep} className="rounded-lg border-2 border-[#C5A028] bg-[#FFFDF5] p-4 space-y-3" data-testid={`departure-edit-form-${d.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-[#C5A028] uppercase tracking-wider">Edit Jadwal</span>
                    <button type="button" onClick={() => setEditDep(null)} className="text-[#8A867C] hover:text-[#141414]"><X size={14} /></button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-[#4A4844]">Tgl Berangkat</label>
                      <input required type="date" className={inp} data-testid="dep-edit-date" value={editDep.depart_date} onChange={(e) => setEditDep({ ...editDep, depart_date: e.target.value })} />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-[#4A4844]">Tgl Kembali</label>
                      <input required type="date" className={inp} data-testid="dep-edit-return" value={editDep.return_date} onChange={(e) => setEditDep({ ...editDep, return_date: e.target.value })} />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-[#4A4844]">Jumlah Kursi</label>
                      <input type="number" min="1" className={inp} data-testid="dep-edit-seats" value={editDep.seats_total} onChange={(e) => setEditDep({ ...editDep, seats_total: e.target.value })} />
                    </div>
                  </div>
                  <div className={`grid gap-3 sm:grid-cols-${Math.min(roomTypes.length, 4)}`}>
                    {roomTypes.map((rt) => (
                      <div key={rt}>
                        <label className="mb-1 block text-xs font-semibold text-[#4A4844]">Harga {rt} (IDR)</label>
                        <input required type="number" className={inp} data-testid={`dep-edit-price-${rt.toLowerCase()}`} value={editDep.prices[rt] ?? ""} onChange={(e) => setEditDep({ ...editDep, prices: { ...editDep.prices, [rt]: e.target.value } })} />
                      </div>))}
                  </div>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setEditDep(null)} className="rounded-lg border border-[#E5DEC9] px-4 py-2 text-xs font-semibold">Batal</button>
                    <button type="submit" className="btn-gold rounded-lg px-5 py-2 text-xs font-bold" data-testid="dep-edit-save">Simpan Perubahan</button>
                  </div>
                </form>
              ) : (
                <div className="flex items-center justify-between rounded-lg border border-[#E5DEC9] p-3 text-sm">
                  <div>
                    <div className="font-semibold">{tanggal(d.depart_date)} → {tanggal(d.return_date)}</div>
                    <div className="text-xs text-[#8A867C]">{d.airline} · kursi {d.seats_booked}/{d.seats_total} · {(d.prices || []).map((x) => `${x.room_type} ${rupiah(x.price)}`).join(" | ")}</div>
                  </div>
                  <div className="flex gap-1">
                    {can("product.write") && (
                      <button onClick={() => openEditDep(d)} data-testid={`departure-edit-${d.id}`}
                        className="rounded-lg border border-[#E5DEC9] p-1.5 text-[#4A4844] hover:bg-[#F4EFE6]" title="Edit jadwal">
                        <Pencil size={13} />
                      </button>)}
                    {can("product.write") && (
                      <button onClick={() => delDeparture(d.id)} data-testid={`departure-delete-${d.id}`} className="rounded-lg border border-[#F0C4C4] p-1.5 text-[#B91C1C]"><Trash2 size={14} /></button>)}
                  </div>
                </div>)}
            </div>))}
          {(p.departures || []).length === 0 && <p className="text-sm text-[#8A867C]">Belum ada jadwal.</p>}
        </div>
        {can("product.write") && (
          <form onSubmit={addDeparture} className="mt-5 space-y-3 rounded-xl bg-[#F4EFE6] p-4" data-testid="departure-form">
            {/* Baris 1: Tanggal & Kursi */}
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-[#4A4844]">Tgl Berangkat</label>
                <input required type="date" className={inp} data-testid="dep-date" value={dep.depart_date} onChange={(e) => setDep({ ...dep, depart_date: e.target.value })} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-[#4A4844]">Tgl Kembali</label>
                <input required type="date" className={inp} data-testid="dep-return" value={dep.return_date} onChange={(e) => setDep({ ...dep, return_date: e.target.value })} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-[#4A4844]">Jumlah Kursi</label>
                <input type="number" className={inp} placeholder="Kursi" data-testid="dep-seats" value={dep.seats_total} onChange={(e) => setDep({ ...dep, seats_total: e.target.value })} />
              </div>
            </div>
            {/* Baris 2: Harga per tipe kamar */}
            <div className={`grid gap-3 sm:grid-cols-${Math.min(roomTypes.length, 4)}`}>
              {roomTypes.map((rt) => (
                <div key={rt}>
                  <label className="mb-1 block text-xs font-semibold text-[#4A4844]">Harga {rt} (IDR)</label>
                  <input
                    required type="number" className={inp}
                    placeholder={`Harga ${rt}`}
                    data-testid={`dep-price-${rt.toLowerCase()}`}
                    value={dep.prices[rt] ?? ""}
                    onChange={(e) => setDep({ ...dep, prices: { ...dep.prices, [rt]: e.target.value } })}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <button className="btn-gold rounded-lg px-5 py-2 text-sm font-bold" data-testid="dep-submit">Tambah Jadwal</button>
            </div>
          </form>)}
        <h3 className="mt-7 font-display text-lg font-semibold">Itinerary</h3>
        <div className="mt-3 space-y-2">
          {[...(p.itineraries || [])].sort((a, b) => a.day - b.day).map((it) => (
            <div key={it.id} data-testid={`itinerary-row-${it.id}`}>
              {editItin?.id === it.id ? (
                <form onSubmit={saveEditItin} className="rounded-lg border-2 border-[#C5A028] bg-[#FFFDF5] p-4 space-y-3" data-testid={`itinerary-edit-form-${it.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-[#C5A028] uppercase tracking-wider">Edit Hari {editItin.day}</span>
                    <button type="button" onClick={() => setEditItin(null)}><X size={14} /></button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-4">
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-[#4A4844]">Hari ke-</label>
                      <input required type="number" min="1" className={inp} data-testid="itin-edit-day" value={editItin.day} onChange={(e) => setEditItin({ ...editItin, day: e.target.value })} />
                    </div>
                    <div className="sm:col-span-3">
                      <label className="mb-1 block text-xs font-semibold text-[#4A4844]">Judul</label>
                      <input required className={inp} data-testid="itin-edit-title" value={editItin.title} onChange={(e) => setEditItin({ ...editItin, title: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-[#4A4844]">Deskripsi</label>
                    <textarea rows={3} className={inp} data-testid="itin-edit-desc" value={editItin.description} onChange={(e) => setEditItin({ ...editItin, description: e.target.value })} />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setEditItin(null)} className="rounded-lg border border-[#E5DEC9] px-4 py-2 text-xs font-semibold">Batal</button>
                    <button type="submit" className="btn-gold rounded-lg px-5 py-2 text-xs font-bold" data-testid="itin-edit-save">Simpan</button>
                  </div>
                </form>
              ) : (
                <div className="flex items-start justify-between rounded-lg border border-[#E5DEC9] p-3 text-sm">
                  <div className="flex gap-3">
                    <span className="mt-0.5 shrink-0 rounded bg-[#141414] px-2 py-0.5 text-xs font-bold text-[#C5A028]">HARI {it.day}</span>
                    <div>
                      <div className="font-semibold text-[#141414]">{it.title}</div>
                      {it.description && <div className="mt-0.5 text-xs text-[#8A867C]">{it.description}</div>}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0 ml-3">
                    {can("product.write") && (
                      <button onClick={() => setEditItin({ id: it.id, day: it.day, title: it.title, description: it.description || "" })}
                        data-testid={`itinerary-edit-${it.id}`}
                        className="rounded-lg border border-[#E5DEC9] p-1.5 text-[#4A4844] hover:bg-[#F4EFE6]" title="Edit">
                        <Pencil size={13} />
                      </button>)}
                    {can("product.write") && (
                      <button onClick={() => delItinerary(it.id)} data-testid={`itinerary-delete-${it.id}`}
                        className="rounded-lg border border-[#F0C4C4] p-1.5 text-[#B91C1C]"><Trash2 size={13} /></button>)}
                  </div>
                </div>)}
            </div>))}
          {(p.itineraries || []).length === 0 && <p className="text-sm text-[#8A867C]">Belum ada itinerary.</p>}
        </div>
        {can("product.write") && (
          <form onSubmit={addItinerary} className="mt-4 space-y-3 rounded-xl bg-[#F4EFE6] p-4" data-testid="itinerary-form">
            <div className="grid gap-3 sm:grid-cols-4">
              <div>
                <label className="mb-1 block text-xs font-semibold text-[#4A4844]">Hari ke-</label>
                <input required type="number" min="1" className={inp} placeholder="1" data-testid="itin-day"
                  value={itin.day} onChange={(e) => setItin({ ...itin, day: e.target.value })} />
              </div>
              <div className="sm:col-span-3">
                <label className="mb-1 block text-xs font-semibold text-[#4A4844]">Judul</label>
                <input required className={inp} placeholder="Keberangkatan Jakarta – Jeddah" data-testid="itin-title"
                  value={itin.title} onChange={(e) => setItin({ ...itin, title: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#4A4844]">Deskripsi (opsional)</label>
              <textarea rows={2} className={inp} placeholder="Deskripsi program hari ini..." data-testid="itin-desc"
                value={itin.description} onChange={(e) => setItin({ ...itin, description: e.target.value })} />
            </div>
            <div className="flex justify-end">
              <button className="btn-gold rounded-lg px-5 py-2 text-sm font-bold" data-testid="itin-submit">Tambah Itinerary</button>
            </div>
          </form>)}

        <h3 className="mt-7 font-display text-lg font-semibold">Riwayat Aktivitas</h3>
        <div className="mt-3"><Timeline items={p.activity || []} /></div>
      </div>
    </div>);
}
